/* eslint-disable deprecation/deprecation */
import { DOCUMENT } from '@angular/common';
import { ApplicationRef, ComponentFactoryResolver, Inject, Injectable, Injector, TemplateRef, Type } from '@angular/core';
import { Subject } from 'rxjs';
import { PopoverComponent } from './popover.component';

export type Content<T> = string | TemplateRef<T> | Type<T>;
export type PopoverPosition = 'right' | 'bottom' | 'cover';
export interface PopoverOptions {
	data?: any;
	panelClass?: string;
	position: PopoverPosition;
	trapFocus?: boolean;
	target: HTMLElement;
}

@Injectable()
export class PopoverService<R = any> {
	nativeElement: HTMLElement;
	position: PopoverPosition;
	target: HTMLElement;
	private readonly afterClosedSubject = new Subject<R | undefined>();

	constructor(
		private readonly applicationRef: ApplicationRef,
		private readonly resolver: ComponentFactoryResolver,
		private readonly injector: Injector,
		@Inject(DOCUMENT) private readonly document: Document
	) {}

	open<T>(content: Content<T>, options: PopoverOptions): PopoverService {
		const { data, position, panelClass, trapFocus = true, target } = options;
		// Only allow one open popup at a time.
		if (this.nativeElement) {
			this.close();
		}

		const factory = this.resolver.resolveComponentFactory(PopoverComponent);
		const ngContent = this.resolveNgContent(content, data);
		const componentRef = factory.create(this.injector, ngContent);

		this.position = position;
		this.target = target;
		componentRef.instance.panelClass = panelClass;
		componentRef.instance.trapFocus = trapFocus;
		componentRef.hostView.detectChanges();

		const { nativeElement } = componentRef.location;

		// keep record of nativeElement for removal
		this.nativeElement = nativeElement;

		// Add the element to the DOM without being visible
		const overlayContainer = this.document.body.getElementsByClassName('cdk-overlay-pane');
		if (overlayContainer.length) {
			//If there is already an open overlay (modal), this element needs to be added to that pane so z-indices work correctly
			overlayContainer[0].appendChild(nativeElement);
		} else {
			this.document.body.appendChild(nativeElement);
		}

		// Position the element based off the width and viewport
		if (panelClass === 'linq-popover-relative') {
			const bcr = target.getBoundingClientRect();

			const body = this.document.body;
			const docEl = this.document.documentElement;

			const scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop;
			const scrollLeft = window.pageXOffset || docEl.scrollLeft || body.scrollLeft;

			const clientTop = docEl.clientTop || body.clientTop || 0;
			const clientLeft = docEl.clientLeft || body.clientLeft || 0;

			let topOffset = 0;
			let leftOffset = 0;
			switch (position) {
				case 'bottom':
					topOffset = bcr.height + 20;
					break;
				case 'right':
					leftOffset = bcr.width + 10;
					break;
			}

			const top  = bcr.top +  scrollTop - clientTop + topOffset;
			const left = bcr.left + scrollLeft - clientLeft + leftOffset;

			nativeElement.firstElementChild.style.left = left + 'px';
			nativeElement.firstElementChild.style.top = top + 'px';
		}
		else {
			this.applyStyles(nativeElement);
		}
		this.activate(nativeElement);

		return this;
	}

	close(data: any | undefined = undefined): void {
		this.afterClosed().next(data);
		if (this.nativeElement) {
			this.nativeElement.remove();
			delete this.nativeElement;
		}
	}

	resolveNgContent<T>(content: Content<T>, data: any = {}): any[][] {
		if (typeof content === 'string') {
			const element = this.document.createTextNode(content);
			return [[element]];
		}

		if (content instanceof TemplateRef) {
			const viewRef = content.createEmbeddedView(data);
			this.applicationRef.attachView(viewRef); // required for context to be passed into template
			return [viewRef.rootNodes];
		}

		const factory = this.resolver.resolveComponentFactory(content);
		const componentRef = factory.create(this.injector);
		Object.entries(data).forEach(([key, value]) => {
			componentRef.instance[key] = value;
		});
		componentRef.hostView.detectChanges();
		return [[componentRef.location.nativeElement]];
	}

	afterClosed(): Subject<R> {
		return this.afterClosedSubject;
	}

	activate(element: HTMLElement): void {
		element.firstElementChild.classList.add('linq-popover--active');
	}

	applyStyles(element: HTMLElement): void {
		const positionProperties = this.calculatePosition(
			this.target.getBoundingClientRect(),
			(element.firstElementChild as HTMLElement).offsetWidth,
			window.innerWidth
		);
		Object.entries(positionProperties).forEach(([prop, value]) => {
			(element.firstElementChild as HTMLElement).style[prop] = value;
		});
	}

	calculatePosition(refBounds: DOMRect, elWidth: number, winWidth: number): { [key: string]: string } {
		// Using a padding safeguard to avoid touching screen edge.
		const safeguard = 32;
		const rightTotal = elWidth + refBounds.right + safeguard;
		const restrainRight = rightTotal > winWidth;

		type PositionProperties = { left?: string; right?: string; top?: string; bottom?: string };
		let position: PositionProperties = {};

		switch (this.position) {
			case 'bottom':
				position = {
					left: `${refBounds.left}px`,
					top: `${refBounds.top + refBounds.height}px`,
				};
				if (restrainRight) {
					position.left = `${refBounds.left + refBounds.width - (rightTotal - winWidth)}px`;
				}
				break;
			case 'right':
				position = {
					top: `${refBounds.top}px`,
					left: `${refBounds.left + refBounds.width}px`,
				};
				break;
			case 'cover':
				position = {
					left: `${refBounds.left}px`,
					top: `${refBounds.top}px`,
				};
				break;
		}

		if (restrainRight) {
			position.left = `${refBounds.left + refBounds.width - (rightTotal - winWidth)}px`;
		}

		return position;
	}
}
